/**
 * Core onboarding types
 * Based on the existing Onboarding.tsx from commander-fe but modernized for the shell app
 */

import type { IcpLocation } from '@commander/utils';

export type UserRole = 'admin' | 'salesperson';

export type CommunicationStyle = 'DEFAULT' | 'CASUAL' | 'FORMAL' | 'OTHER';

export type LocationType = IcpLocation;

export interface EmailSettings {
  signature: string;
  communicationStyle: CommunicationStyle;
  communicationStyleText?: string;
}

export interface InviteUser {
  fullName: string;
  email: string;
}

export interface CompanyProfile {
  company_name: string;
  domain: string;
  products?: string[];
}

export interface CompanyICP {
  locations?: LocationType[];
  size_ranges?: any[];
  business_sectors?: Array<{
    sector: string;
    subsectors: Array<{ name: string }>;
  }>;
  job_titles?: string[];
}

export interface OnboardingFormData {
  // Basic info
  company: string;
  domain: string;

  // Multi-select fields
  locations: string[];
  services: string[];
  size: string[];
  industries: string[];
  jobTitles: string[];

  // Complex objects
  emailSettings: EmailSettings;

  // Arrays
  integrations: string[];
  inviteUsers: InviteUser[];
}

export interface OnboardingStep {
  id: string;
  title: string;
  content_title?: string;
  description: string;
  component: string; // Component name
  validator: (data: OnboardingFormData) => ValidationResult;
  isOptional?: boolean;
  dependsOnRole?: UserRole[];
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export interface OnboardingState {
  currentStep: number;
  selectedRole: UserRole | null;
  isCompleted: boolean;
  formData: OnboardingFormData;
  isLoading: boolean;
  error: string | null;
  isDirty: boolean;
  lastSaved: Date | null;
  autoSaveEnabled: boolean;
}

export interface OnboardingWizardProps {
  onComplete: () => void;
  onExit: () => void;
  initialStep?: number;
  role?: UserRole;
}

export interface StepProps {
  data: OnboardingFormData;
  onChange: (updates: Partial<OnboardingFormData>) => void;
  validation: (data: any) => Promise<ValidationResult>;
  onNext: () => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
  selectedRole: UserRole;
}

export interface FormInputProps {
  label: string;
  type: 'text' | 'email' | 'url' | 'textarea';
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  className?: string;
}

export interface TagInputProps {
  tags: string[];
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  suggestions?: string[];
  placeholder?: string;
  error?: string;
  label?: string;
  className?: string;
}

export interface StepNavigationProps {
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  onSkip?: () => void;
  isLoading: boolean;
  canProceed: boolean;
  showSkip?: boolean;
  nextButtonText?: string;
}

export interface StepProgressProps {
  steps: OnboardingStep[];
  currentStep: number;
  completedSteps: number[];
  className?: string;
}

// API Response types
export interface ApiResponse<T = any> {
  status: number;
  data?: T;
  message?: string;
  success?: boolean;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: UserRole;
  step_number?: number;
  onboarding_complete?: boolean;
  company_profile_builder_completed?: boolean;
  company_employee?: {
    company: {
      id: string;
      name?: string;
      domain_name?: string;
      cro_admin_user_id?: string;
    };
  };
}

// Constants
export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'company',
    title: 'Company',
    content_title: 'What company do you work at?',
    description: 'Fill in the text box below',
    component: 'CompanyInfoStep',
    validator: (data) => {
      if (!data.company.trim()) {
        return { isValid: false, error: 'Please enter your company name' };
      }
      if (!data.domain.trim()) {
        return { isValid: false, error: 'Please enter your domain' };
      }
      return { isValid: true };
    },
  },
  {
    id: 'locations',
    title: 'Locations',
    content_title: 'What geographic locations do you serve?',
    description: 'Select or add all that apply',
    component: 'LocationsStep',
    validator: (data) => ({
      isValid: data.locations.length > 0,
      error: data.locations.length === 0 ? 'Please add at least one location' : undefined,
    }),
  },
  {
    id: 'services',
    title: 'Services',
    content_title: 'What services do you offer?',
    description: 'Select or add all that apply',
    component: 'ServicesStep',
    validator: (data) => ({
      isValid: data.services.length > 0,
      error: data.services.length === 0 ? 'Please add at least one service' : undefined,
    }),
  },
  {
    id: 'size',
    title: 'Size',
    content_title: 'What is the company size of your ideal customer?',
    description: 'Select all that apply',
    component: 'SizeStep',
    validator: (data) => ({
      isValid: data.size.length > 0,
      error: data.size.length === 0 ? 'Please select at least one company size' : undefined,
    }),
  },
  {
    id: 'industries',
    title: 'Industries',
    content_title: 'What industries are your customers in?',
    description: 'Select all that apply',
    component: 'IndustriesStep',
    validator: (data) => ({
      isValid: data.industries.length > 0,
      error: data.industries.length === 0 ? 'Please add at least one industry' : undefined,
    }),
  },
  {
    id: 'jobTitles',
    title: 'Job Titles',
    content_title: 'What job titles you would like to target?',
    description: 'Select or add all that apply',
    component: 'JobTitlesStep',
    validator: (data) => ({
      isValid: data.jobTitles.length > 0,
      error: data.jobTitles.length === 0 ? 'Please add at least one job title' : undefined,
    }),
  },
  {
    id: 'emailSettings',
    title: 'Email Settings',
    content_title: 'Set up your email signature and communication style',
    description: 'Customize how you appear in email communications',
    component: 'EmailSettingsStep',
    validator: (data) => {
      if (!data.emailSettings.signature.trim()) {
        return { isValid: false, error: 'Please enter your email signature' };
      }
      if (
        data.emailSettings.communicationStyle === 'OTHER' &&
        !data.emailSettings.communicationStyleText?.trim()
      ) {
        return {
          isValid: false,
          error: 'Please provide a sample email for the custom communication style',
        };
      }
      return { isValid: true };
    },
  },
  {
    id: 'previewLeads',
    title: 'Preview Leads',
    content_title: 'Preview Leads',
    description: 'Here are 2 leads generated based off of the ICP metrics you added.',
    component: 'PreviewLeadsStep',
    validator: () => ({ isValid: true }), // Optional step
  },
  {
    id: 'integrations',
    title: 'Integrations',
    content_title: 'Connect your accounts',
    description: 'Select accounts',
    component: 'IntegrationsStep',
    validator: () => ({ isValid: true }), // Optional step
    isOptional: true,
  },
  {
    id: 'invite',
    title: 'Invite',
    description: 'Invite users to your Commander team',
    component: 'InviteUsersStep',
    validator: (data) => ({
      isValid: data.inviteUsers.length > 0,
      error: data.inviteUsers.length === 0 ? 'Please add at least one user to invite' : undefined,
    }),
    dependsOnRole: ['admin'], // Only for admin users
  },
];

export const INITIAL_ONBOARDING_DATA: OnboardingFormData = {
  company: '',
  domain: '',
  locations: [],
  services: [],
  size: [],
  industries: [],
  jobTitles: [],
  emailSettings: {
    signature: '',
    communicationStyle: 'DEFAULT',
    communicationStyleText: '',
  },
  integrations: [],
  inviteUsers: [],
};
